import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Brand } from '../components/Brand';
import { go, PUBLIC_PATH_FOR, type PublicRoute } from '../lib/router';

/**
 * The public site's frame: a sticky header, the page, a footer.
 *
 * Deliberately not the application's Shell. The sidebar is an operational
 * navigation for someone who has signed in and has an organisation; showing it
 * to a visitor would be both meaningless and a claim about what they can
 * reach. The two layouts share the brand, the colour tokens and the type
 * scale, and nothing else.
 */

interface NavLink {
  label: string;
  href: string;
  /** Anchors within the home page, rather than routes of their own. */
  anchor?: boolean;
}

/**
 * The main navigation.
 *
 * Seven items is more than a marketing site usually wants, and it is the right
 * number here because of who reads it: a practice manager arrives asking "what
 * does this do and what does it cost", and an information governance lead
 * arrives asking "where does our data go" — and the second one will not find
 * a Security page hidden in a footer, they will assume there is nothing to
 * find.
 *
 * "Book a demo" is in the header actions rather than in this list, so it stays
 * a button at every width instead of becoming the seventh identical link.
 *
 * Funding Radar had a top-level slot here and is now absent from the public
 * site entirely — it is built but has no curated board of opportunities behind
 * it, and a capability advertised before it works is the one claim that would
 * make a visitor doubt the rest of the page. It returns when there is a board.
 */
const LINKS: NavLink[] = [
  { label: 'Why InBrief', href: '/#why', anchor: true },
  { label: 'How it works', href: '/#how-it-works', anchor: true },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Security', href: '/security' },
  { label: 'Resources', href: '/resources' },
  { label: 'Services', href: '/services' },
  { label: 'About', href: '/about' },
];

/**
 * An internal link. Left as a real anchor with a real href so that middle
 * click, "open in new tab" and a screen reader's link list all behave, and
 * intercepted only for an ordinary left click.
 */
export function Anchor({ to, children, className, onClick }: {
  to: string;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <a
      href={to}
      className={className}
      onClick={(e) => {
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        onClick?.();
        if (to.startsWith('/#')) {
          const id = to.slice(2);
          if (window.location.pathname === '/') {
            /*
              Scrolled on the next frame, not on this line.

              `onClick` above closes the mobile menu, and that is a React state
              update: the menu is still in the document when the next statement
              runs. It is an in-flow panel rather than an overlay, so it is
              holding several hundred pixels of height that are about to
              disappear — the scroll was aimed at a layout that no longer
              existed by the time it arrived, which is why different links
              landed in the same wrong place and why it felt random.

              One frame is enough for React to have flushed and the header to
              have collapsed back to its 68 pixels.
            */
            requestAnimationFrame(() => {
              document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
            history.replaceState({}, '', to);
            return;
          }
        }
        go(to);
      }}
    >
      {children}
    </a>
  );
}

export const route = (name: PublicRoute) => PUBLIC_PATH_FOR[name];

function Header() {
  const [open, setOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);

  // Escape closes the mobile menu and returns focus to the control that
  // opened it, which is where a keyboard user expects to be.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        menuButton.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <header className="mk-header">
      <div className="mk-container mk-header-inner">
        <Anchor to="/" className="mk-brand" onClick={() => setOpen(false)}>
          <Brand />
          <span className="visually-hidden">InBrief home</span>
        </Anchor>

        <nav className="mk-nav-desktop" aria-label="Main">
          <ul>
            {LINKS.map((link) => (
              <li key={link.href}><Anchor to={link.href}>{link.label}</Anchor></li>
            ))}
          </ul>
        </nav>

        <div className="mk-header-actions">
          <Anchor to="/login" className="mk-login">Log in</Anchor>
          <Anchor to="/contact" className="btn btn-primary btn-small">Book a demo</Anchor>
        </div>

        <button
          ref={menuButton}
          type="button"
          className="mk-menu-toggle"
          aria-expanded={open}
          aria-controls="mk-mobile-nav"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="visually-hidden">{open ? 'Close menu' : 'Open menu'}</span>
          <span aria-hidden="true">{open ? '✕' : '☰'}</span>
        </button>
      </div>

      <nav
        id="mk-mobile-nav"
        className={`mk-nav-mobile${open ? ' is-open' : ''}`}
        aria-label="Main"
        hidden={!open}
      >
        <ul>
          {LINKS.map((link) => (
            <li key={link.href}>
              <Anchor to={link.href} onClick={() => setOpen(false)}>{link.label}</Anchor>
            </li>
          ))}
          <li><Anchor to="/login" onClick={() => setOpen(false)}>Log in</Anchor></li>
          <li>
            <Anchor to="/contact" className="btn btn-primary" onClick={() => setOpen(false)}>
              Book a demo
            </Anchor>
          </li>
        </ul>
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <footer className="mk-footer">
      <div className="mk-container mk-footer-inner">
        <div>
          <Brand />
          <p className="mk-footer-line">
            Contract intelligence and patient communications for UK primary care.
          </p>
        </div>

        <nav aria-label="Product">
          <h2 className="mk-footer-heading">Product</h2>
          <ul>
            <li><Anchor to="/#why">Why InBrief</Anchor></li>
            <li><Anchor to="/#how-it-works">How it works</Anchor></li>
            <li><Anchor to="/demo">Interactive demo</Anchor></li>
            <li><Anchor to="/pricing">Pricing</Anchor></li>
            <li><Anchor to="/services">Services</Anchor></li>
          </ul>
        </nav>

        <nav aria-label="Company">
          <h2 className="mk-footer-heading">Company</h2>
          <ul>
            <li><Anchor to="/about">About</Anchor></li>
            <li><Anchor to="/security">Security and governance</Anchor></li>
            <li><Anchor to="/resources">Resources and FAQs</Anchor></li>
            <li><Anchor to="/contact">Book a demo</Anchor></li>
            <li><Anchor to="/login">Log in</Anchor></li>
          </ul>
        </nav>
      </div>

      <div className="mk-container">
        <p className="mk-footer-small">
          InBrief is a software product for primary care organisations. It is not a medical device,
          it gives no clinical advice, and it is not affiliated with or endorsed by the NHS.
          All prices exclude VAT.
        </p>
      </div>
    </footer>
  );
}

export function MarketingShell({ children }: { children: ReactNode }) {
  return (
    <div className="mk">
      <a className="mk-skip" href="#main">Skip to content</a>
      <Header />
      <main id="main">{children}</main>
      <Footer />
    </div>
  );
}
